import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Box, Select, MenuItem, Typography, Paper } from '@mui/material';
import { createStringXY } from 'ol/coordinate.js';
import { transform } from 'ol/proj';
import { modernDesignSystem } from '../constants/themes';

const CoordinateInfo = () => {
  const { map } = useSelector((state) => state.globalMaps[0] || {});
  const [coordinates, setCoordinates] = useState('');
  const [selectedProjection, setSelectedProjection] = useState('EPSG:32637');

  // Koordinat sistemleri tanımları
  const projectionSystems = [
    { code: 'EPSG:32637', name: 'UTM Zone 37N' },
    { code: 'EPSG:4326', name: 'WGS84' },
    { code: 'EPSG:3857', name: 'Web Mercator' },
  ];

  useEffect(() => {
    if (!map) return;

    const handlePointerMove = (event) => {
      try {
        // OpenLayers'da doğru yöntem: pixel'dan coordinate hesaplama
        const pixel = [event.offsetX, event.offsetY];
        const coordinate = map.getCoordinateFromPixel(pixel);

        if (coordinate && coordinate.length === 2) {
          // Seçilen koordinat sistemine dönüştür
          const transformedCoordinate = transform(
            coordinate,
            map.getView().getProjection(),
            selectedProjection
          );

          // Koordinat formatını belirle
          let formatFunction;
          if (selectedProjection === 'EPSG:4326') {
            // WGS84 için derece formatı
            formatFunction = createStringXY(6); // 6 ondalık basamak
          } else {
            // UTM ve Web Mercator için metre formatı
            formatFunction = createStringXY(2); // 2 ondalık basamak
          }

          const formattedCoordinates = formatFunction(transformedCoordinate);
          setCoordinates(formattedCoordinates);
        }
      } catch (error) {
        console.error('Koordinat hesaplama hatası:', error);
        // Hata durumunda boş bırak, "Koordinat bekleniyor..." görünsün
      }
    };

    // Mouse move event listener ekle - doğrudan map container'a
    const mapContainer = map.getTargetElement();
    if (mapContainer) {
      mapContainer.addEventListener('mousemove', handlePointerMove);
      
      return () => {
        // Cleanup
        mapContainer.removeEventListener('mousemove', handlePointerMove);
      };
    }
  }, [map, selectedProjection]);

  const handleProjectionChange = (event) => {
    setSelectedProjection(event.target.value);
  };

  const getSelectedProjectionName = () => {
    const projection = projectionSystems.find(proj => proj.code === selectedProjection);
    return projection ? projection.name : selectedProjection;
  };

  const getCoordinateLabel = () => {
    switch (selectedProjection) {
      case 'EPSG:4326':
        return 'Enlem, Boylam:';
      case 'EPSG:32637':
        return 'X, Y (m):';
      case 'EPSG:3857':
        return 'X, Y (m):';
      default:
        return 'Koordinat:';
    }
  };

  if (!map) return null;

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'fixed',
        bottom: '5px',
        right: '5px',
        zIndex: 1000,
        backgroundColor: 'rgba(255, 255, 255, 0.85)', // Daha şeffaf
        backdropFilter: 'blur(15px)', // Daha güçlü blur
        border: `1px solid ${modernDesignSystem.colors.neutral.gray300}`,
        borderRadius: modernDesignSystem.borders.radius.small,
        padding: modernDesignSystem.spacing.sm,
        width: '410px', // Sabit genişlik
        boxShadow: modernDesignSystem.shadows.medium,
        
        // Tablet modunda gizle
        display: 'block',
        '@media (max-width: 1024px)': {
          display: 'none',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: modernDesignSystem.spacing.sm,
        }}
      >
        {/* Koordinat Sistemi Seçici */}
        <Select
          value={selectedProjection}
          onChange={handleProjectionChange}
          size="small"
          variant="outlined"
          sx={{
            minWidth: '130px', // Daha dar select
            fontSize: '12px',
            '& .MuiOutlinedInput-input': {
              padding: '4px 8px',
              fontSize: '12px',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: modernDesignSystem.colors.neutral.gray400,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: modernDesignSystem.colors.primary.main,
            },
            '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: modernDesignSystem.colors.primary.main,
            }
          }}
        >
          {projectionSystems.map((projection) => (
            <MenuItem 
              key={projection.code} 
              value={projection.code}
              sx={{ fontSize: '12px' }}
            >
              {projection.name}
            </MenuItem>
          ))}
        </Select>

        {/* Koordinat Bilgisi */}
        <Box sx={{ 
          flex: 1, 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: modernDesignSystem.spacing.xs,
        }}>
          <Typography
            variant="body1"
            sx={{
              fontSize: '15px', // Daha büyük font
              fontWeight: 600,
              color: modernDesignSystem.colors.neutral.gray800,
              fontFamily: 'monospace',
              lineHeight: 1.2,
              flex: 1,
            }}
          >
            {coordinates || 'Koordinat bekleniyor...'}
          </Typography>
          
          <Typography
            variant="caption"
            sx={{
              fontSize: '10px',
              color: modernDesignSystem.colors.neutral.gray500,
              fontWeight: 500,
              whiteSpace: 'nowrap',
            }}
          >
            {getCoordinateLabel()}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default CoordinateInfo;
